import React from 'react';
import { Loader2 } from 'lucide-react';

/**
 * Unified platform action button — single source of truth for all
 * action-row buttons across the app. Blue + white only.
 *
 * Variants:
 *   "primary"   → solid blue fill, white text (default high-emphasis action)
 *   "secondary" → outlined blue (mid-emphasis — used as "off" state for toggles
 *                 and for less-emphasized but still actionable buttons)
 *   "neutral"   → outlined white/grey (low-emphasis — secondary toggles,
 *                 cancel-like actions, dimmed state)
 *
 * States:
 *   loading={true}  → spinner replaces icon, button disabled
 *   disabled={true} → opacity-40, no pointer events
 *
 * Sizing:
 *   "md" (default) → px-3 py-1.5 text-xs
 *   "sm"           → px-2 py-1 text-[10px]
 *
 * Always: same height/padding/text/icon size, no text wrap, scale-on-press.
 * Pass any additional props (data-testid, title, onClick, etc.) through.
 */
const VARIANT_CLASSES = {
  primary:
    'bg-ai-accent text-white hover:bg-ai-accent/80',
  secondary:
    'border border-ai-accent/30 bg-ai-accent/10 text-ai-accent hover:bg-ai-accent/20',
  neutral:
    'border border-white/15 bg-white/5 text-white/70 hover:bg-white/10 hover:text-white/90',
};

const SIZE_CLASSES = {
  md: 'px-3 py-1.5 text-xs',
  sm: 'px-2 py-1 text-[10px]',
};

const ICON_SIZE = {
  md: 'w-3.5 h-3.5',
  sm: 'w-3 h-3',
};

export default function ActionButton({
  variant = 'primary',
  size = 'md',
  icon: Icon,
  loading = false,
  disabled = false,
  onClick,
  title,
  className = '',
  children,
  ...rest
}) {
  const isDisabled = disabled || loading;
  const iconCls = ICON_SIZE[size] || ICON_SIZE.md;
  return (
    <button
      onClick={onClick}
      disabled={isDisabled}
      title={title}
      className={`${SIZE_CLASSES[size] || SIZE_CLASSES.md} rounded-lg font-medium transition-all flex items-center gap-2 whitespace-nowrap active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed ${VARIANT_CLASSES[variant] || VARIANT_CLASSES.primary} ${className}`}
      {...rest}
    >
      {loading
        ? <Loader2 className={`${iconCls} animate-spin`} />
        : (Icon ? <Icon className={iconCls} /> : null)}
      {children}
    </button>
  );
}
